<template>
	<div class="c-admin-api-access-chart">
		<p class="c-admin-api-access-chart__detail" role="status">
			<template v-if="activePoint">
				<strong>{{ activePoint.date }} (UTC)</strong>:
				{{ activePoint.successCount }} successful,
				{{ activePoint.failureCount }} failed
			</template>
			<template v-else>Hover, tap, or focus a day to see its counts.</template>
		</p>
		<div class="c-admin-api-access-chart__legend">
			<span class="c-admin-api-access-chart__success">Successful</span>
			<span class="c-admin-api-access-chart__failure">Failed</span>
		</div>
		<div class="c-admin-api-access-chart__scroll">
			<svg viewBox="0 0 720 300" role="group" aria-label="Daily API calls by result">
				<title>Daily successful and failed API calls</title>
				<g v-for="tick in ticks" :key="tick.value">
					<line x1="48" x2="700" :y1="tick.y" :y2="tick.y" class="grid" />
					<text x="40" :y="tick.y + 4" text-anchor="end">{{ tick.value }}</text>
				</g>
				<polyline :points="successLine" class="series success" />
				<polyline :points="failureLine" class="series failure" />
				<g v-for="point in dateLabels" :key="point.date">
					<text :x="point.x" y="282" :text-anchor="point.anchor">
						{{ point.date.slice(5) }}
					</text>
				</g>
				<g v-if="activePoint">
					<circle :cx="activePoint.x" :cy="activePoint.successY" r="4" class="point success" />
					<circle :cx="activePoint.x" :cy="activePoint.failureY" r="4" class="point failure" />
				</g>
				<rect
					v-for="(point, index) in points"
					:key="point.date"
					:x="point.x - pointSpacing / 2"
					y="16"
					:width="pointSpacing"
					height="244"
					class="hit-area"
					tabindex="0"
					:aria-label="`${point.date} UTC: ${point.successCount} successful, ${point.failureCount} failed`"
					@mouseenter="activeIndex = index"
					@mouseleave="activeIndex = null"
					@focus="activeIndex = index"
					@blur="activeIndex = null"
					@click="activeIndex = index"
				>
					<title>{{ point.date }} UTC: {{ point.successCount }} successful, {{ point.failureCount }} failed</title>
				</rect>
			</svg>
		</div>
	</div>
</template>

<script>
export default {
	name: 'AdminApiAccessChart',
	props: { days: { type: Array, required: true } },
	data() {
		return { activeIndex: null };
	},
	computed: {
		step() {
			let maximum = 0;
			for (const day of this.days) {
				maximum = Math.max(maximum, day.successCount, day.failureCount);
			}
			return Math.max(1, Math.ceil(maximum / 4));
		},
		ticks() {
			const ticks = [];
			for (let i = 0; i <= 4; i++) {
				ticks.push({ value: i * this.step, y: 260 - i * 60 });
			}
			return ticks;
		},
		pointSpacing() {
			return 652 / Math.max(1, this.days.length - 1);
		},
		points() {
			return this.days.map((day, index) => ({
				date: day.date,
				successCount: day.successCount,
				failureCount: day.failureCount,
				x: 48 + index * this.pointSpacing,
				successY: 260 - (day.successCount / (this.step * 4)) * 240,
				failureY: 260 - (day.failureCount / (this.step * 4)) * 240,
			}));
		},
		successLine() {
			return this.points.map((point) => `${point.x},${point.successY}`).join(' ');
		},
		failureLine() {
			return this.points.map((point) => `${point.x},${point.failureY}`).join(' ');
		},
		dateLabels() {
			const interval = Math.max(1, Math.ceil((this.points.length - 1) / 4));
			return this.points.filter((point, index) => index % interval === 0 || index === this.points.length - 1);
		},
		activePoint() {
			return this.activeIndex === null ? null : this.points[this.activeIndex];
		},
	},
};
</script>

<style lang="scss">
.c-admin-api-access-chart {
	&__detail {
		min-height: 2.5em;
		margin: var(--margin-lg) 0 0;
		font-size: var(--font-size-sm);
	}

	&__legend {
		display: flex;
		gap: var(--margin-lg);
		font-size: var(--font-size-sm);
	}

	&__legend span::before {
		content: '';
		display: inline-block;
		width: 1rem;
		height: 2px;
		margin-right: var(--margin-sm);
		vertical-align: middle;
		background: var(--color-primary);
	}

	&__legend &__failure::before {
		background: var(--color-danger);
	}

	&__scroll {
		overflow-x: auto;
	}

	svg {
		display: block;
		width: 100%;
		min-width: 520px;
	}

	text {
		fill: var(--color-font);
		font-size: 12px;
	}

	.grid {
		stroke: var(--color-bg-5);
		stroke-width: 1;
	}

	.series {
		fill: none;
		stroke-width: 2;
		vector-effect: non-scaling-stroke;
	}

	.success {
		stroke: var(--color-primary);
	}

	.failure {
		stroke: var(--color-danger);
	}

	.point.success {
		fill: var(--color-primary);
	}

	.point.failure {
		fill: var(--color-danger);
	}

	.hit-area {
		fill: transparent;
		cursor: crosshair;

		&:focus {
			outline: none;
			stroke: var(--color-primary);
			stroke-width: 1;
		}
	}
}
</style>
